//Stores
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { temp_schemes } from 'src/stores/lists/scenes-derived';
import { get } from 'svelte/store';
//Utils
import { _deepClone } from '../../../helpers/utilities';
import { findDeviceSensorID, findExtTempSensor } from 'src/components/TemperatureScheme/helpers/utils';
//Types
import type { ITempSchemeObj } from 'src/types/TemperatureSchemeType';
//Helpers
import { status_configurator } from '../../../helpers/device_data_helpers';
import { extractHexId } from 'src/components/TemperatureScheme/helpers/temp_scheme_to_ui';

/**
 * Retrieves a thermostat object by its ID.
 * @param id - The ID of the thermostat.
 * @returns A deep-cloned thermostat object or undefined if not found.
 */
const getThermostat = (id: string): ITempSchemeObj | undefined =>
	get(temp_schemes)[id] ? (_deepClone(get(temp_schemes)[id]) as ITempSchemeObj) : undefined;

/**
 * Retrieves the sensor ID associated with a thermostat.
 * @param thermostat - The thermostat object.
 * @returns The sensor ID or undefined if not available.
 */
const getDeviceSensorId = (thermostat: ITempSchemeObj): string | undefined => {
	let sensorDecId = typeof thermostat === 'object' ? thermostat?.tschem?.r.split(':')[1] : undefined;
	return findDeviceSensorID(sensorDecId, thermostat);
};

/**
 * Retrieves a device sensor by its ID.
 * @param id - The ID of the device sensor.
 * @returns A device sensor object or a placeholder object if not found.
 */
const getDeviceSensor = (id: string) =>
	get(devices)[id] || {
		id,
		deleted: true,
	};

/**
 * Retrieves the status of a device sensor.
 * @param deviceSensor - The device sensor object.
 * @returns The status of the device sensor or undefined if not found.
 */
const getStatusDeviceSensor = (deviceSensor) =>
	deviceSensor ? devices_status.getByRealId(deviceSensor.id) : undefined;

/**
 * Retrieves the settings of a device sensor.
 * @param deviceSensor - The device sensor object.
 * @returns The settings of the device sensor or undefined if not found.
 */
const getSettingsDeviceSensor = (deviceSensor) =>
	deviceSensor ? devices_settings.getByRealId(deviceSensor.id) : undefined;

/**
 * Retrieves the status and settings of a device sensor.
 * @param deviceSensor - The device sensor object.
 * @returns An object containing the status and settings of the device sensor.
 */
const getSensorInfo = (deviceSensor) => ({
	statusDeviceSensor: getStatusDeviceSensor(deviceSensor),
	settingsDeviceSensor: getSettingsDeviceSensor(deviceSensor),
});

/**
 * Retrieves the actuators of a thermostat.
 * @param thermostat - The thermostat object.
 * @returns An array of device sensor objects.
 */
const getActuators = (thermostat) =>
	typeof thermostat === 'object' && thermostat.toheat?.blk
		? thermostat.toheat.blk
				.map(({ r }) => extractHexId(r))
				.filter((id) => id && (get(devices)[id]?.category === 'module' || !id.includes('0000')))
				.map((id) => getDeviceSensor(id))
		: [];

/**
 * Checks if a thermostat has any missing devices or sensors.
 * @param id - The ID of the thermostat.
 * @returns True if there are missing devices or sensors; otherwise, false.
 */
export const hasMissingThermostatDevice = (id: string): boolean => {
	const thermostat = getThermostat(id);
	const deviceSensorId = getDeviceSensorId(thermostat);
	const deviceSensor = getDeviceSensor(deviceSensorId);
	const { statusDeviceSensor, settingsDeviceSensor } = getSensorInfo(deviceSensor);
	const extSensorsStatuses = status_configurator(
		statusDeviceSensor,
		deviceSensor,
		settingsDeviceSensor,
	);
	const extTempSensor = findExtTempSensor(extSensorsStatuses, thermostat, deviceSensor);
	const actuators = getActuators(thermostat);

	return !deviceSensor || !extTempSensor || actuators.some((actuator) => actuator.deleted);
};
